import { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import logo from "../../../assets/logo.svg";
import { NAV_ICONS, IconLogout } from "../../ui/icons";
import ds from "./desktopSidebar.module.css";

function navClass({ isActive }) {
  return isActive ? `${ds.navLink} ${ds.navLinkActive}` : ds.navLink;
}

function moreLinkClass({ isActive }) {
  return isActive ? `${ds.moreMenuLink} ${ds.moreMenuLinkActive}` : ds.moreMenuLink;
}

function splitSections(sections) {
  const primary = [];
  const more = [];
  for (const section of sections) {
    if (section.label) {
      more.push(...(section.items || []));
    } else {
      primary.push(...(section.items || []));
    }
  }
  return { primary, more };
}

function NavItems({ items, getBadge, className = navClass }) {
  return items.map((item) => {
    const Icon = NAV_ICONS[item.icon] || NAV_ICONS.overview;
    const badge = getBadge?.(item);
    return (
      <NavLink
        key={item.id || item.to}
        to={item.to}
        end={item.end}
        className={className}
      >
        <Icon className={ds.navIcon} />
        <span className={ds.navLabel}>{item.label}</span>
        {badge > 0 ? (
          <span className={ds.navBadge}>{badge > 9 ? "9+" : badge}</span>
        ) : null}
      </NavLink>
    );
  });
}

function MoreMenu({ items, getBadge }) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const isActive = useMemo(
    () =>
      items.some(
        (item) =>
          pathname === item.to ||
          (item.to !== "/" && pathname.startsWith(`${item.to}/`)),
      ),
    [items, pathname],
  );

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  if (!items.length) return null;

  return (
    <div className={ds.moreWrap} ref={wrapRef}>
      <button
        type="button"
        className={`${ds.moreBtn} ${open ? ds.moreBtnOpen : ""} ${isActive ? ds.moreBtnActive : ""}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
      >
        Ещё
        <span className={ds.moreChevron} aria-hidden>▾</span>
      </button>
      {open ? (
        <div className={ds.moreMenu} role="menu">
          <NavItems items={items} getBadge={getBadge} className={moreLinkClass} />
        </div>
      ) : null}
    </div>
  );
}

export default function DesktopSidebar({
  homeTo = "/",
  brandName,
  sections = [],
  profile,
  profileTo,
  onLogout,
  getBadge,
  headerActions = null,
}) {
  const { primary, more } = useMemo(() => splitSections(sections), [sections]);

  const initials = String(profile?.full_name || "?")
    .trim()
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const userChip = (
    <>
      <div className={ds.userAvatar} aria-hidden>
        {initials}
      </div>
      <span className={ds.userName}>{profile?.full_name || "—"}</span>
    </>
  );

  return (
    <header className={ds.header}>
      <div className={ds.headerInner}>
        <Link to={homeTo} className={ds.brand}>
          <img src={logo} alt="" className={ds.brandLogo} width={28} height={28} />
          <div className={ds.brandText}>
            <span className={ds.brandName}>{brandName}</span>
          </div>
        </Link>

        <nav className={ds.nav} aria-label="Основная навигация">
          <NavItems items={primary} getBadge={getBadge} />
          <MoreMenu items={more} getBadge={getBadge} />
        </nav>

        <div className={ds.headerActions}>
          {headerActions}
          {profileTo ? (
            <Link
              to={profileTo}
              className={ds.userChip}
              title="Профиль"
              aria-label="Профиль"
            >
              {userChip}
            </Link>
          ) : (
            <div className={ds.userChip}>{userChip}</div>
          )}
          {onLogout ? (
            <button
              type="button"
              className={ds.logoutBtn}
              onClick={onLogout}
              title="Выйти"
              aria-label="Выйти"
            >
              <IconLogout className={ds.navIcon} />
            </button>
          ) : null}
        </div>
      </div>
    </header>
  );
}

export { ds as desktopSidebarStyles };
